import React, { Component } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { HTTPService } from '../../../services/httpService';
import { getLocationName, getLocationBadgeColor } from '../../../util/locationUtils';

const API = new HTTPService();

/**
 * TransferStaffModal - Modal for transferring a staff member to another location
 *
 * Props:
 * - show: Boolean
 * - onHide: Function
 * - onSuccess: Function - called after successful transfer
 * - staff: Object - { User_id, user_name, location_id, locationId }
 * - locations: Array - all company locations
 */
class TransferStaffModal extends Component {
  state = {
    targetLocationId: '',
    loading: false,
    error: null
  };

  componentDidUpdate(prevProps) {
    if (this.props.show && !prevProps.show) {
      this.setState({ targetLocationId: '', loading: false, error: null });
    }
  }

  handleSubmit = async () => {
    const { staff, onSuccess, onHide } = this.props;
    const { targetLocationId } = this.state;

    if (!targetLocationId) {
      this.setState({ error: 'Please select a destination location.' });
      return;
    }

    try {
      this.setState({ loading: true, error: null });
      await API.patch(`/users/${staff.User_id}/location`, { locationId: targetLocationId, companyId: this.props.companyId });
      onHide();
      if (onSuccess) onSuccess(staff);
    } catch (err) {
      console.error('Transfer error:', err);
      const errorMsg = err.response?.data?.message || 'Failed to transfer staff member.';
      this.setState({ error: errorMsg, loading: false });
    }
  };

  render() {
    const { show, onHide, staff, locations } = this.props;
    const { targetLocationId, loading, error } = this.state;

    if (!staff) return null;

    const currentLocId = staff.location_id || staff.locationId;
    const currentLoc = locations?.find(l => String(l.location_id) === String(currentLocId));
    const otherLocations = (locations || []).filter(l => String(l.location_id) !== String(currentLocId));

    return (
      <Modal show={show} onHide={onHide} centered backdrop={loading ? 'static' : true}>
        <Modal.Header closeButton={!loading}>
          <Modal.Title>
            <i className="fa-sharp fa-regular fa-arrow-right-arrow-left text-warning mr-2"></i>
            Transfer Staff Member
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>
            Transfer <strong>{staff.user_name}</strong> to a different location.
          </p>

          {/* Current Location */}
          <div className="form-group">
            <label className="font-weight-bold">Current Location</label>
            <div>
              {currentLoc ? (
                <span
                  className="badge"
                  style={{
                    backgroundColor: getLocationBadgeColor(currentLocId, locations),
                    color: 'var(--color-text-inverse)',
                    fontSize: '0.9rem',
                    padding: '6px 10px'
                  }}
                >
                  {getLocationName(currentLoc)}
                </span>
              ) : (
                <span className="text-muted">Unknown</span>
              )}
            </div>
          </div>

          {/* Destination */}
          <div className="form-group">
            <label className="font-weight-bold">
              Transfer To <span className="text-danger">*</span>
            </label>
            <select
              className="form-control"
              value={targetLocationId}
              onChange={(e) => this.setState({ targetLocationId: e.target.value, error: null })}
              disabled={loading}
            >
              <option value="">Select a location...</option>
              {otherLocations.map(l => (
                <option key={l.location_id} value={l.location_id}>
                  {getLocationName(l)}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div className="alert alert-danger mb-0">
              <i className="fa-sharp fa-regular fa-exclamation-circle mr-2"></i>
              {error}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={this.handleSubmit}
            disabled={loading || !targetLocationId}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2"></span>
                Transferring...
              </>
            ) : (
              <>
                <i className="fa-sharp fa-regular fa-arrow-right-arrow-left mr-2"></i>
                Transfer
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default TransferStaffModal;
